"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { Icon } from "@/app/components/Icon";
import { motion, AnimatePresence } from "framer-motion";
import { Faq } from "@/app/components/Faq";
import { getContactData } from "./contact_data";

export default function ContactClient({ locale = "en" }: { locale?: string }) {
  const t = getContactData(locale);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    organisation: "",
    message: "",
    captcha: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    phone: "",
    email: "",
    organisation: "",
    message: "",
    captcha: "",
  });

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [captchaTask, setCaptchaTask] = useState({ a: 0, b: 0, result: 0 });
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const generateCaptcha = useCallback(() => {
    const a = Math.floor(Math.random() * 10);
    const b = Math.floor(Math.random() * 10);
    setCaptchaTask({ a, b, result: a + b });
    setFormData(prev => ({ ...prev, captcha: "" }));
    setErrors(prev => ({ ...prev, captcha: "" }));
  }, []);

  const drawCaptcha = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.fillStyle = "#f8fafc";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.font = "bold 24px 'Geist Mono', monospace";
    ctx.fillStyle = "rgba(5, 60, 97, 0.4)";
    ctx.textBaseline = "middle";
    ctx.setTransform(1.1, 0.1, -0.1, 1, 0, 0);
    ctx.fillText(`${captchaTask.a} + ${captchaTask.b} = ?`, 10, canvas.height / 2);
    for (let i = 0; i < 4; i++) {
      ctx.strokeStyle = "rgba(5, 60, 97, 0.15)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.stroke();
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }, [captchaTask]);

  useEffect(() => {
    generateCaptcha();
  }, [generateCaptcha]);

  useEffect(() => {
    drawCaptcha();
  }, [drawCaptcha]);

  // Auto-hide success toast after 4 seconds
  useEffect(() => {
    if (status === "success") {
      const timer = setTimeout(() => setStatus("idle"), 2000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      name: "",
      phone: "",
      email: "",
      organisation: "",
      message: "",
      captcha: "",
    };

    // Name validation
    const cleanedName = formData.name.trim();
    if (!cleanedName) {
      newErrors.name = t.errNameRequired;
      isValid = false;
    } else if (cleanedName.length < 2) {
      newErrors.name = t.errNameShort;
      isValid = false;
    } else if (!/^[a-zA-Z\s'.]+$/.test(cleanedName)) {
      newErrors.name = t.errNameInvalid;
      isValid = false;
    }

    // Phone validation
    const digitsOnly = formData.phone.replace(/\D/g, "");
    if (!formData.phone.trim()) {
      newErrors.phone = t.errPhoneRequired;
      isValid = false;
    } else if (digitsOnly.length !== 10) {
      newErrors.phone = t.errPhoneShort;
      isValid = false;
    } else if (!/^[6-9]\d{9}$/.test(digitsOnly)) {
      newErrors.phone = t.errPhoneIndian;
      isValid = false;
    }

    // Email validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const trimmedEmail = formData.email.trim();
    if (!trimmedEmail) {
      newErrors.email = t.errEmailRequired;
      isValid = false;
    } else if (!emailRegex.test(trimmedEmail)) {
      newErrors.email = t.errEmailInvalid;
      isValid = false;
    }

    // Organisation validation (optional)
    // (no mandatory check — left blank is fine)

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = t.errMessageRequired;
      isValid = false;
    } else if (formData.message.trim().length < 5) {
      newErrors.message = t.errMessageShort;
      isValid = false;
    }

    // Captcha validation
    if (!formData.captcha) {
      newErrors.captcha = t.errCaptchaRequired;
      isValid = false;
    } else if (parseInt(formData.captcha) !== captchaTask.result) {
      newErrors.captcha = t.errCaptchaIncorrect;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setStatus("submitting");
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          contact_number: formData.phone,
          email: formData.email,
          designation: "NA",
          organisation_name: formData.organisation.trim() || "NA",
          org_type: "NA",
          requirement: formData.message,
          source: "chikitsa",
        }),
      });
      if (response.ok) {
        setStatus("success");
        setFormData({ name: "", phone: "", email: "", organisation: "", message: "", captcha: "" });
        generateCaptcha();
      } else { setStatus("error"); }
    } catch { setStatus("error"); }
  };

  return (
    <div className="contact-page section section--tight" style={{ paddingTop: "calc(var(--section-py, 48px) + 24px)" }}>
      <div className="container">
        <div className="section-head section-head--center reveal">
          <span className="eyebrow" style={{ marginBottom: 16 }}>
            <span className="dot"></span> {t.eyebrow}
          </span>
          <h1>{t.h1}</h1>
          <p className="lede">
            {t.lede}
          </p>
        </div>

        <div className="badge-container reveal" style={{ "--reveal-delay": "100ms", flexDirection: "column", alignItems: "center", gap: 12 } as any}>
          <span className="badge badge--xl">{t.badge}</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
            style={{ color: "var(--rose)", display: "flex", justifyContent: "center" }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14M19 12l-7 7-7-7" />
            </svg>
          </motion.div>
        </div>

        <div className="contact-main-grid reveal" style={{ "--reveal-delay": "200ms" } as any}>
          {/* Info Side (Left) */}
          <div className="contact-visual">
            <div className="map-overlay"></div>
            <div className="visual-content">
              <h2>{t.infoTitle}</h2>
              <p className="visual-lede">
                {t.infoLede}
              </p>

              <div className="visual-details">
                <a href="tel:+919509917266" className="v-item">
                  <Icon name="phone" />
                  <span>+91 9509917266</span>
                </a>
                <a href="mailto:connect@chikitsa.io" className="v-item">
                  <Icon name="mail" />
                  <span>connect@chikitsa.io</span>
                </a>
                <div className="v-item v-item--top">
                  <Icon name="pin" />
                  <div className="v-address">
                    <div className="address-block">
                      <strong>{t.puneOffice}</strong>
                      <p>{t.puneAddress}</p>
                    </div>
                    <div className="address-block">
                      <strong>{t.jaipurOffice}</strong>
                      <p>{t.jaipurAddress}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form Side (Right) */}
          <div className="contact-form-side">
            <form className="modern-form" onSubmit={handleSubmit} noValidate>
              <div className="form-field">
                <div className={`field-inner${errors.name ? " has-error" : ""}`}>
                  <div className="field-icon"><Icon name="user" /></div>
                  <div className="field-sep"></div>
                  <input
                    type="text"
                    required
                    placeholder={t.placeholderName}
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value });
                      if (errors.name) setErrors(prev => ({ ...prev, name: "" }));
                    }}
                  />
                </div>
                {errors.name && <span className="field-error-msg">{errors.name}</span>}
              </div>

              <div className="form-field">
                <div className={`field-inner${errors.phone ? " has-error" : ""}`}>
                  <div className="field-icon"><Icon name="phone" /></div>
                  <div className="field-sep"></div>
                  <input
                    type="tel"
                    required
                    maxLength={10}
                    placeholder={t.placeholderPhone}
                    value={formData.phone}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                      setFormData({ ...formData, phone: value });
                      if (errors.phone) setErrors(prev => ({ ...prev, phone: "" }));
                    }}
                  />
                </div>
                {errors.phone && <span className="field-error-msg">{errors.phone}</span>}
              </div>

              <div className="form-field">
                <div className={`field-inner${errors.email ? " has-error" : ""}`}>
                  <div className="field-icon"><Icon name="mail" /></div>
                  <div className="field-sep"></div>
                  <input
                    type="email"
                    required
                    placeholder={t.placeholderEmail}
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value });
                      if (errors.email) setErrors(prev => ({ ...prev, email: "" }));
                    }}
                  />
                </div>
                {errors.email && <span className="field-error-msg">{errors.email}</span>}
              </div>

              <div className="form-field">
                <div className={`field-inner${errors.organisation ? " has-error" : ""}`}>
                  <div className="field-icon"><Icon name="building" /></div>
                  <div className="field-sep"></div>
                  <input
                    type="text"
                    placeholder="Organisation Name (optional)"
                    value={formData.organisation}
                    onChange={(e) => {
                      setFormData({ ...formData, organisation: e.target.value });
                      if (errors.organisation) setErrors(prev => ({ ...prev, organisation: "" }));
                    }}
                  />
                </div>
                {errors.organisation && <span className="field-error-msg">{errors.organisation}</span>}
              </div>

              <div className="form-field">
                <textarea
                  required
                  rows={4}
                  className={`placeholder-faint${errors.message ? " has-error" : ""}`}
                  placeholder={t.placeholderMessage}
                  value={formData.message}
                  onChange={(e) => {
                    setFormData({ ...formData, message: e.target.value });
                    if (errors.message) setErrors(prev => ({ ...prev, message: "" }));
                  }}
                ></textarea>
                {errors.message && <span className="field-error-msg">{errors.message}</span>}
              </div>

              <div className="captcha-section">
                <label>{t.captchaLabel}</label>
                <div className="captcha-row">
                  <div className="captcha-box">
                    <canvas ref={canvasRef} width={140} height={44} />
                  </div>
                  <input
                    type="text"
                    inputMode="numeric"
                    required
                    className={`captcha-input-wide${errors.captcha ? " has-error" : ""}`}
                    placeholder={t.placeholderCaptcha}
                    value={formData.captcha}
                    onChange={(e) => {
                      setFormData({ ...formData, captcha: e.target.value });
                      if (errors.captcha) setErrors(prev => ({ ...prev, captcha: "" }));
                    }}
                  />
                  <button type="button" className="refresh-btn" onClick={generateCaptcha} aria-label="Refresh Captcha">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 11-9-9c2.5 0 4.7 1 6.3 2.7L21 8m0 0V3m0 5h-5" /></svg>
                  </button>
                </div>
                {errors.captcha && <span className="field-error-msg">{errors.captcha}</span>}
              </div>

              <div className="submit-row">
                <button
                  type="submit"
                  className="btn btn--brand btn--lg btn--padding-fix"
                  disabled={status === "submitting"}
                >
                  {status === "submitting" ? t.btnSending : t.btnSubmit}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Consistent FAQ Section */}
        <Faq
          items={t.faqs}
          eyebrow={t.faqEyebrow}
          heading={t.faqHeading}
          intro={t.faqIntro}
        />
      </div>

      {/* Temporary Success Toast */}
      <AnimatePresence>
        {status === "success" && (
          <motion.div
            initial={{ opacity: 0, y: 50, x: "-50%" }}
            animate={{ opacity: 1, y: 0, x: "-50%" }}
            exit={{ opacity: 0, y: 20, x: "-50%" }}
            transition={{ duration: 0.2 }}
            className="toast-badge success"
          >
            <div className="toast-inner">
              <Icon name="check" />
              <span>{t.successToast}</span>
            </div>
          </motion.div>
        )}
        {status === "error" && (
          <motion.div
            initial={{ opacity: 0, y: 50, x: "-50%" }}
            animate={{ opacity: 1, y: 0, x: "-50%" }}
            exit={{ opacity: 0, y: 20, x: "-50%" }}
            transition={{ duration: 0.2 }}
            className="toast-badge error"
          >
            <div className="toast-inner">
              <span>{t.errorToast}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
